import React, { useState, useEffect, useCallback } from 'react';
import { Search, Edit2, Users, UserCheck, Mail, Phone, Briefcase, Building2, X, Check, Save, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import Swal from 'sweetalert2';
import config from "../config";

const CustomerList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const API_URL = `${config.API_BASE_URL}/Users`;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL, {
        headers: { 'accept': 'text/plain' }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch users',
        background: '#1e293b',
        color: '#f1f5f9',
        confirmButtonColor: '#3b82f6'
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openEditModal = (user) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleInputChange = (field, value) => {
    setEditingUser(prev => ({ ...prev, [field]: value }));
  };

  const updateUser = async () => {
    try {
      const response = await fetch(`${API_URL}/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(editingUser)
      });

      if (response.ok) {
        await fetchUsers();
        closeEditModal();
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'User updated successfully',
          background: '#1e293b',
          color: '#f1f5f9',
          confirmButtonColor: '#10b981'
        });
      } else {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update user',
        background: '#1e293b',
        color: '#f1f5f9',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  // Unique departments for filter
  const uniqueDepts = [...new Set(users.map(u => u.departmentId))].filter(Boolean).sort((a, b) => a - b);

  // Filter
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.positionId?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && user.isActive === 1) ||
      (filterStatus === 'inactive' && user.isActive === 0);

    const matchesDept =
      filterDept === 'all' || user.departmentId === parseInt(filterDept);

    return matchesSearch && matchesStatus && matchesDept;
  });

  // Sort
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase() ?? '';
      bVal = bVal?.toLowerCase() ?? '';
    }
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  // Pagination
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const currentUsers = sortedUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || 'U';
  };

  const getDeptColor = (deptId) => {
    const colors = {
      1: '#3b82f6', 2: '#8b5cf6', 3: '#10b981', 4: '#f59e0b',
      5: '#ef4444', 6: '#06b6d4', 7: '#84cc16', 8: '#ec4899',
      9: '#f97316', 10: '#6366f1'
    };
    return colors[deptId] || '#64748b';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      padding: '2rem',
      fontFamily: '"Inter", -apple-system, sans-serif'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        .user-card {
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid #334155;
          border-radius: 16px;
          padding: 1.5rem;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .user-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #10b981, #3b82f6);
        }
        .user-card:hover {
          transform: translateY(-4px);
          border-color: #10b981;
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.2);
        }
        .search-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 3rem;
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 12px;
          color: #f1f5f9;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          box-sizing: border-box;
        }
        .search-input:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        .search-input::placeholder { color: #64748b; }
        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          border: none;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: 'Inter', sans-serif;
          font-size: 0.95rem;
        }
        .btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }
        .btn-success:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4); }
        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4); }
        .btn-secondary { background: #334155; color: #f1f5f9; }
        .btn-secondary:hover { background: #475569; }
        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modal-content {
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 20px;
          max-width: 820px;
          width: 95%;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .input-field {
          width: 100%;
          padding: 0.75rem;
          background: #0f172a;
          border: 1px solid #334155;
          border-radius: 8px;
          color: #f1f5f9;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          box-sizing: border-box;
        }
        .input-field:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        .input-field:disabled { opacity: 0.5; cursor: not-allowed; }
        label {
          display: block;
          margin-bottom: 0.4rem;
          color: #94a3b8;
          font-weight: 500;
          font-size: 0.85rem;
        }
        .badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .badge-primary { background: rgba(59,130,246,0.1); color: #60a5fa; border: 1px solid rgba(59,130,246,0.2); }
        .badge-success { background: rgba(16,185,129,0.1); color: #34d399; border: 1px solid rgba(16,185,129,0.2); }
        .badge-danger { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
        .badge-warning { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.2); }
        .select-field {
          padding: 0.625rem 2.5rem 0.625rem 1rem;
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 8px;
          color: #f1f5f9;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23f1f5f9' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.875rem center;
          width: 100%;
        }
        .select-field:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
        .spinner {
          border: 3px solid #334155;
          border-top: 3px solid #10b981;
          border-radius: 50%;
          width: 40px; height: 40px;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .page-btn {
          padding: 0.5rem 0.875rem;
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 8px;
          color: #f1f5f9;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          font-family: 'Inter', sans-serif;
          min-width: 40px;
        }
        .page-btn:hover:not(:disabled) { background: #334155; border-color: #10b981; transform: translateY(-1px); }
        .page-btn.active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-color: #10b981;
          color: white;
        }
        .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .sort-button {
          background: rgba(16,185,129,0.1);
          border: 1px solid rgba(16,185,129,0.2);
          color: #34d399;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          justify-content: center;
          font-family: 'Inter', sans-serif;
        }
        .sort-button:hover { background: rgba(16,185,129,0.2); }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #1e293b; }
        ::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #64748b; }
        .toggle-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.4rem;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
        }
        .toggle-btn:hover { background: rgba(255,255,255,0.05); }
      `}</style>

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.5rem',
              letterSpacing: '-0.02em'
            }}>
              Customer List
            </h1>
            <p style={{ color: '#64748b', fontSize: '1.05rem' }}>
              Manage and view all registered users
            </p>
          </div>
          <a href="/add-customer" style={{ textDecoration: 'none' }}>
       
          </a>
        </div>

        {/* Filters */}
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{ position: 'relative', maxWidth: '500px', marginBottom: '1rem' }}>
            <Search size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input
              type="text"
              placeholder="Search by name, username, email, or position..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="search-input"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label>Filter by Status</label>
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} className="select-field">
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label>Filter by Department</label>
              <select value={filterDept} onChange={(e) => { setFilterDept(e.target.value); setCurrentPage(1); }} className="select-field">
                <option value="all">All Departments</option>
                {uniqueDepts.map(d => (
                  <option key={d} value={d}>Department {d}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Sort by</label>
              <select value={sortField} onChange={(e) => setSortField(e.target.value)} className="select-field">
                <option value="id">ID</option>
                <option value="firstName">First Name</option>
                <option value="lastName">Last Name</option>
                <option value="username">Username</option>
                <option value="email">Email</option>
                <option value="departmentId">Department</option>
                <option value="positionId">Position</option>
              </select>
            </div>
            <div>
              <label>Order</label>
              <button onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')} className="sort-button">
                {sortDirection === 'asc' ? '↑ Ascending' : '↓ Descending'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div className="badge badge-primary"><Users size={14} />Total: {users.length}</div>
            <div className="badge badge-success"><UserCheck size={14} />Filtered: {filteredUsers.length}</div>
            <div className="badge badge-warning">Page {currentPage} of {totalPages || 1}</div>
          </div>
        </div>

        {/* Cards */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem', gap: '1rem' }}>
            <div className="spinner"></div>
            <p style={{ color: '#64748b' }}>Loading users...</p>
          </div>
        ) : currentUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px' }}>
            <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
              {searchTerm || filterStatus !== 'all' || filterDept !== 'all'
                ? 'No users found matching your filters.'
                : 'No users found.'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              {currentUsers.map((user) => (
                <div key={user.id} className="user-card">
                  {/* Card Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      {user.profileBase64 ? (
                        <img
                          src={`data:image/jpeg;base64,${user.profileBase64}`}
                          alt={`${user.firstName} ${user.lastName}`}
                          style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '12px',
                          background: `linear-gradient(135deg, ${getDeptColor(user.departmentId)}, #0f172a)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: 'white',
                          flexShrink: 0,
                          border: `2px solid ${getDeptColor(user.departmentId)}40`
                        }}>
                          {getInitials(user.firstName, user.lastName)}
                        </div>
                      )}
                      <div>
                        <h3 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.2rem' }}>
                          {user.firstName} {user.lastName}
                        </h3>
                        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>@{user.username}</p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span className={`badge ${user.isActive === 1 ? 'badge-success' : 'badge-danger'}`}>
                        {user.isActive === 1 ? <Check size={12} /> : <X size={12} />}
                        {user.isActive === 1 ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => openEditModal(user)}
                        style={{
                          background: 'rgba(16,185,129,0.1)',
                          border: '1px solid rgba(16,185,129,0.2)',
                          borderRadius: '8px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          color: '#34d399',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16,185,129,0.2)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16,185,129,0.1)'}
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Card Info */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Mail size={15} color="#3b82f6" />
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Email:</span>
                      <span style={{ color: '#f1f5f9', fontSize: '0.85rem' }}>{user.email}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Briefcase size={15} color="#8b5cf6" />
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Position:</span>
                      <span style={{ color: '#f1f5f9', fontSize: '0.85rem', fontWeight: '500' }}>{user.positionId || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Building2 size={15} color="#10b981" />
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Department:</span>
                      <span style={{
                        color: getDeptColor(user.departmentId),
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        Dept. {user.departmentId}
                      </span>
                    </div>
                    {user.contactNo && user.contactNo !== '-' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Phone size={15} color="#f59e0b" />
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Contact:</span>
                        <span style={{ color: '#f1f5f9', fontSize: '0.85rem' }}>{user.contactNo}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer badges */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '1rem', borderTop: '1px solid #334155' }}>
                    <span className="badge badge-primary">ID: {user.id}</span>
                    <span className="badge badge-warning">Level: {user.userLevelId}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '16px',
                padding: '1.5rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="page-btn">First</button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="page-btn">Previous</button>
                {[...Array(totalPages)].map((_, i) => {
                  const p = i + 1;
                  if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                    return (
                      <button key={p} onClick={() => setCurrentPage(p)} className={`page-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>
                    );
                  } else if (p === currentPage - 2 || p === currentPage + 2) {
                    return <span key={p} style={{ color: '#64748b' }}>...</span>;
                  }
                  return null;
                })}
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="page-btn">Next</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="page-btn">Last</button>
              </div>
            )}
          </>
        )}

        {/* Edit Modal */}
        {showEditModal && editingUser && (
          <div className="modal-overlay" onClick={closeEditModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div style={{
                padding: '2rem',
                borderBottom: '1px solid #334155',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '20px 20px 0 0'
              }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#f1f5f9', marginBottom: '0.25rem' }}>
                    Edit User
                  </h2>
                  <p style={{ color: '#64748b', fontSize: '0.9rem' }}>@{editingUser.username}</p>
                </div>
                <button onClick={closeEditModal} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.5rem' }}>
                  <X size={24} />
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }}>
                  <div>
                    <label>ID</label>
                    <input type="text" value={editingUser.id} disabled className="input-field" />
                  </div>
                  <div>
                    <label>Username</label>
                    <input type="text" value={editingUser.username} disabled className="input-field" />
                  </div>
                  <div>
                    <label>First Name</label>
                    <input type="text" value={editingUser.firstName || ''} onChange={(e) => handleInputChange('firstName', e.target.value)} className="input-field" />
                  </div>
                  <div>
                    <label>Last Name</label>
                    <input type="text" value={editingUser.lastName || ''} onChange={(e) => handleInputChange('lastName', e.target.value)} className="input-field" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Email</label>
                    <input type="email" value={editingUser.email || ''} onChange={(e) => handleInputChange('email', e.target.value)} className="input-field" />
                  </div>
                  <div>
                    <label>Position</label>
                    <input type="text" value={editingUser.positionId || ''} onChange={(e) => handleInputChange('positionId', e.target.value)} className="input-field" />
                  </div>
                  <div>
                    <label>Contact No.</label>
                    <input type="text" value={editingUser.contactNo || ''} onChange={(e) => handleInputChange('contactNo', e.target.value)} className="input-field" />
                  </div>
                  <div>
                    <label>Department ID</label>
                    <input type="number" value={editingUser.departmentId || ''} onChange={(e) => handleInputChange('departmentId', parseInt(e.target.value))} className="input-field" />
                  </div>
                  <div>
                    <label>User Level ID</label>
                    <input type="number" value={editingUser.userLevelId || ''} onChange={(e) => handleInputChange('userLevelId', parseInt(e.target.value))} className="input-field" />
                  </div>

                  {/* isActive Toggle */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{
                      padding: '1rem',
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div>
                        <p style={{ color: '#f1f5f9', fontWeight: '500', marginBottom: '0.2rem' }}>Active Status</p>
                        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {editingUser.isActive === 1 ? 'User is currently active' : 'User is currently inactive'}
                        </p>
                      </div>
                      <button
                        className="toggle-btn"
                        onClick={() => handleInputChange('isActive', editingUser.isActive === 1 ? 0 : 1)}
                      >
                        {editingUser.isActive === 1
                          ? <ToggleRight size={40} color="#10b981" />
                          : <ToggleLeft size={40} color="#64748b" />
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #334155',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button onClick={closeEditModal} className="btn btn-secondary">Cancel</button>
                <button onClick={updateUser} className="btn btn-success">
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerList;